import React, { useState, useEffect, useContext } from 'react';
import { Card } from '../utils';
import { jwtDecode } from 'jwt-decode';
import { getKycApiCallRecords, getCompanyDetails } from '../../../API/Api';
import { toast } from 'react-toastify';
import { TokenContext } from '../../../App';

const ApiCount = () => {
  const [encodedAccessToken] = useContext(TokenContext);
  const decodedToken = jwtDecode(encodedAccessToken);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 1
  });

  const fetchRecords = async (page = 1) => {
    try {
      setLoading(true);
      // Fetch company details first
      const companyDetailsResponse = await getCompanyDetails('company');
      setCompanyDetails(companyDetailsResponse.data);
      
      // Then fetch API call records
      const result = await getKycApiCallRecords({
        page,
        pageSize: pagination.pageSize
      });
      const response = result.data;
      setRecords(response.data);
      setPagination({
        ...pagination,
        page,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      });
    } catch (error) {
      toast.error('Failed to fetch API call records');
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchRecords(newPage);
    }
  };

  const renderPagination = () => {
    const pageButtons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous page button
    pageButtons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(pagination.page - 1)}
        disabled={pagination.page === 1}
        className="px-3 py-1 border rounded mx-1 disabled:opacity-50"
      >
        Previous
      </button>
    );

    // Page number buttons
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 border rounded mx-1 ${pagination.page === i ? 'bg-blue-500 text-white' : ''}`}
        >
          {i}
        </button>
      );
    }

    // Next page button
    pageButtons.push(
      <button
        key="next"
        onClick={() => handlePageChange(pagination.page + 1)}
        disabled={pagination.page === pagination.totalPages}
        className="px-3 py-1 border rounded mx-1 disabled:opacity-50"
      >
        Next
      </button>
    );

    return pageButtons;
  };

  return (
    <div>
      {/* Company Usage Card - Single row layout */}
      {companyDetails && (
        <Card className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <span className="text-gray-600 font-medium">Name Searches Used:</span>
                <span className="ml-2 font-bold">
                  {companyDetails?.kycSettings?.apiCallsUsed || 0} / {companyDetails?.kycSettings?.apiRateLimit || 0}
                </span>
              </div>
              <div className="border-l border-gray-300 h-6"></div>
              <div>
                <span className="text-gray-600 font-medium">Next Reset Date:</span>
                <span className="ml-2 font-bold">
                  {companyDetails?.kycSettings?.nextResetDate || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Main Content Card */}
      <Card>
        <h1 className="text-2xl font-bold mb-6">KYC API Call Records</h1>
        
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-2 px-4 border">Name</th>
                    <th className="py-2 px-4 border">API Source</th>
                    <th className="py-2 px-4 border">Question</th>
                    <th className="py-2 px-4 border">API Count</th>
                    <th className="py-2 px-4 border">API Remaining</th>
                    <th className="py-2 px-4 border">Invocation time</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr key={record._id} className="hover:bg-gray-50">
                      <td className="py-2 px-4 border text-center">{record.name}</td>
                      <td className="py-2 px-4 border text-center">{record.apiSource}</td>
                      <td className="py-2 px-4 border max-w-xs truncate text-center">
                        {record.question}
                      </td>
                      <td className="py-2 px-4 border text-center">
                        {record.apiCount}
                      </td>
                      <td className="py-2 px-4 border text-center">{record.apiRemaining}</td>
                      <td className="py-2 px-4 border text-center">
                        {record.createDate}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-600">
                Total {pagination.total} records, up to 50 records per page
              </div>
              <div className="flex">
                {renderPagination()}
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default ApiCount;